import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React, { useEffect, useMemo, useState } from 'react'
import BaseButton from 'src/components/Buttons/BaseButton'
import ModalDrawer from 'src/components/ModalDrawer'
import {
  formatSizeNumber,
  getSizeArea,
  SIZE_GUIDE_CATEGORIES,
  SizeGuideCategory
} from 'src/components/Modals/StorageSizeModal/constants'
import SizeIllustration from 'src/components/Modals/StorageSizeModal/SizeIllustration'
import { RedirectUrls } from 'src/types/types'
import ga from 'src/utils/pixels/gtag'

const ALL_SIZE_KEYS = SIZE_GUIDE_CATEGORIES.flatMap((cat) =>
  cat.sizes.map((s) => s.i18nKey)
).filter((key, i, arr) => arr.indexOf(key) === i)

/** Where the modal was opened from. Used as a PostHog breakdown so we can
 * tell which surface (listing detail vs listing size section) drives the
 * most modal interactions and where users actually convert. New mount
 * points MUST pass an explicit surface — never default it inline. */
type StorageSizeModalSurface = 'listing_detail' | 'listing_size_section'

type StorageSizeModalProps = {
  open: boolean
  handleClose: (open: boolean) => void
  defaultCategory?: string
  defaultSizeId?: string
  surface: StorageSizeModalSurface
}

const StorageSizeModal = ({
  open,
  handleClose,
  defaultCategory,
  defaultSizeId,
  surface
}: StorageSizeModalProps) => {
  const { t } = useTranslation('size_guide')
  const [activeTabId, setActiveTabId] = useState(
    defaultCategory ?? SIZE_GUIDE_CATEGORIES[0].id
  )
  const activeCategory =
    SIZE_GUIDE_CATEGORIES.find((c) => c.id === activeTabId) ??
    SIZE_GUIDE_CATEGORIES[0]
  const [activeSizeId, setActiveSizeId] = useState(
    defaultSizeId ?? activeCategory.sizes[0].id
  )

  // Fire `size_modal_open` once each time the modal transitions to open.
  // Carries the entry surface + initial category/size so we can slice
  // open-volume by where users came from and what they started looking at.
  useEffect(() => {
    if (!open) return
    ga.event('size_modal_open', {
      surface,
      default_category: defaultCategory ?? SIZE_GUIDE_CATEGORIES[0].id,
      default_size_id: defaultSizeId ?? null
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleTabChange = (cat: SizeGuideCategory) => {
    ga.event('size_modal_category_change', {
      surface,
      from_category: activeTabId,
      to_category: cat.id
    })
    setActiveTabId(cat.id)
    setActiveSizeId(cat.sizes[0].id)
  }

  const activeSize =
    activeCategory.sizes.find((s) => s.id === activeSizeId) ??
    activeCategory.sizes[0]

  const area = getSizeArea(activeSize.dimensions)

  const tabLabels: Record<string, string> = {
    SMALL: t('TABS.SMALL'),
    MEDIUM: t('TABS.MEDIUM'),
    LARGE: t('TABS.LARGE'),
    VEHICLE: t('TABS.VEHICLE'),
    FLEET: t('TABS.FLEET')
  }

  const sizeData = useMemo(() => {
    const data: Record<
      string,
      { nickname: string; fits: string[]; idealFor: string }
    > = {}
    for (const key of ALL_SIZE_KEYS) {
      data[key] = {
        nickname: t(`SIZES.${key}.NICKNAME` as never),
        fits: t(`SIZES.${key}.FITS` as never, {
          returnObjects: true
        }) as string[],
        idealFor: t(`SIZES.${key}.IDEAL_FOR` as never)
      }
    }
    return data
  }, [t])

  const selected = sizeData[activeSize.i18nKey]
  const fits = Array.isArray(selected?.fits) ? selected.fits : []

  const editorialTitle = (
    <div>
      <p className="font-display text-[10px] tracking-[0.22em] text-blue-800 uppercase md:text-[11px]">
        {t('TITLE')}
      </p>
      <h2 className="mt-1.5 font-display text-[22px] leading-[1.15] font-medium tracking-[-0.005em] text-ink-700 md:text-[26px]">
        {t('PAGE.UNIT_GUIDE_TITLE')}
      </h2>
      <p className="mt-1 text-[13px] leading-relaxed text-ink-200">
        {t('SUBTITLE')}
      </p>
    </div>
  )

  return (
    <ModalDrawer
      isOpen={open}
      setIsOpen={handleClose}
      customTitle={editorialTitle}
      width="55%"
    >
      <div className="flex flex-col gap-6 pt-2 pb-2">
        {/* Category tabs — hairline underline */}
        <div
          role="tablist"
          aria-label={t('PAGE.UNIT_GUIDE_TITLE')}
          className="scrollbar-hide -mx-1 flex gap-1 overflow-x-auto border-b border-ink-100/15 px-1"
        >
          {SIZE_GUIDE_CATEGORIES.map((cat) => {
            const active = activeTabId === cat.id
            return (
              <button
                key={cat.id}
                role="tab"
                aria-selected={active}
                type="button"
                onClick={() => handleTabChange(cat)}
                className={`relative -mb-px shrink-0 px-3 py-2.5 text-[13px] font-medium tracking-[0.04em] whitespace-nowrap transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 ${
                  active ? 'text-ink-700' : 'text-ink-200 hover:text-ink-700'
                }`}
              >
                {tabLabels[cat.i18nKey]}
                {active && (
                  <span
                    aria-hidden="true"
                    className="absolute right-2 bottom-0 left-2 h-px bg-blue-500"
                  />
                )}
              </button>
            )
          })}
        </div>

        {/* Size selector — hairline-bordered tiles, italic Fraunces numerals */}
        <div className="grid grid-cols-3 gap-2 sl:grid-cols-4">
          {activeCategory.sizes.map((size) => {
            const areaLabel = formatSizeNumber(getSizeArea(size.dimensions))
            const isActive = activeSizeId === size.id
            return (
              <button
                key={size.id}
                type="button"
                aria-pressed={isActive}
                onClick={() => {
                  ga.event('size_modal_size_select', {
                    surface,
                    category: activeTabId,
                    size_id: size.id
                  })
                  setActiveSizeId(size.id)
                }}
                className={`flex flex-col items-start gap-0.5 border px-3 py-2.5 text-left transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 ${
                  isActive
                    ? 'border-blue-500 bg-blue-500/5'
                    : 'border-ink-100/15 bg-white-500 hover:border-ink-100/40 hover:bg-cream-300'
                }`}
              >
                <span className="flex items-baseline gap-1">
                  <span
                    className={`font-display text-[18px] leading-none font-medium tracking-[-0.01em] italic ${
                      isActive ? 'text-blue-800' : 'text-ink-700'
                    }`}
                  >
                    {areaLabel}
                  </span>
                  <span className="text-[10px] tracking-[0.06em] text-ink-200">
                    m²
                  </span>
                </span>
                <span className="mt-0.5 line-clamp-1 text-[11px] leading-tight text-ink-200">
                  {sizeData[size.i18nKey]?.nickname}
                </span>
              </button>
            )
          })}
        </div>

        {/* Detail — image plate (cream surround) + body */}
        <div className="flex flex-col gap-5 md:flex-row md:items-start md:gap-7">
          <div className="md:w-1/2">
            <SizeIllustration
              size={activeSize}
              alt={selected?.nickname ?? ''}
              sizes="(max-width: 900px) 100vw, 28vw"
            />
          </div>

          <div className="flex-1">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
              <h3 className="font-display text-[22px] leading-tight font-medium tracking-[-0.005em] text-ink-700 md:text-[24px]">
                {selected?.nickname}
              </h3>
              <span className="font-display text-[16px] leading-tight font-medium text-blue-800 italic md:text-[18px]">
                {`${formatSizeNumber(area)} m²`}
              </span>
            </div>

            <p className="mt-5 font-display text-[10px] tracking-[0.22em] text-blue-800 uppercase md:text-[11px]">
              {t('WHAT_FITS')}
            </p>
            <ul className="mt-3 space-y-2.5">
              {fits.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-[13px] leading-relaxed text-ink-700 md:text-[14px]"
                >
                  <CheckRoundedIcon
                    className="mt-0.5 text-[15px] text-blue-500"
                    fontSize="inherit"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-5 grid grid-cols-1 gap-1 border-t border-ink-100/15 pt-4 sl:grid-cols-12 sl:gap-4">
              <p className="font-display text-[10px] tracking-[0.22em] text-blue-800 uppercase sl:col-span-3 md:text-[11px]">
                {t('IDEAL_FOR')}
              </p>
              <p className="text-[13px] leading-relaxed text-ink-700 sl:col-span-9 md:text-[14px]">
                {selected?.idealFor}
              </p>
            </div>
          </div>
        </div>

        {/* Disclaimer + actions — anchored on a hairline */}
        <div className="border-t border-ink-100/15 pt-4">
          <p className="text-[11px] leading-relaxed text-ink-200 italic">
            {t('DISCLAIMER')}
          </p>
        </div>

        <BaseButton
          rounded
          variant="contained"
          fullWidth
          href={`${RedirectUrls.Search}?sizes=${activeSize.sizeFilter}`}
          endIcon={<ArrowForwardIcon className="text-[16px]" />}
          onClick={() =>
            ga.event('size_modal_cta_click', {
              surface,
              cta: 'search_by_size',
              category: activeTabId,
              size_id: activeSize.id,
              size_filter: activeSize.sizeFilter
            })
          }
        >
          {t('CTA.SEARCH_SIZE')}
        </BaseButton>

        <Link
          href={RedirectUrls.SizeGuide}
          className="group inline-flex items-center justify-center gap-1.5 self-center text-[12px] font-medium tracking-[0.08em] text-ink-700 uppercase underline decoration-blue-500/40 underline-offset-[6px] transition-colors hover:text-blue-800 hover:decoration-blue-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
          onClick={() =>
            ga.event('size_modal_cta_click', {
              surface,
              cta: 'open_size_guide',
              category: activeTabId,
              size_id: activeSize.id
            })
          }
        >
          {t('CTA_QUESTION')}
          <NorthEastRoundedIcon
            className="text-[14px] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            fontSize="inherit"
          />
        </Link>
      </div>
    </ModalDrawer>
  )
}

export default React.memo(StorageSizeModal)
